import {
  Autocomplete,
  Button,
  Checkbox,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  IconButton,
  InputAdornment,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Tooltip,
  Typography,
} from "@mui/material";
import { Add, DeleteOutlined, Search } from "@mui/icons-material";
import { API_URL } from "const/constants";
import axios from "axios";
import { useEffect, useMemo, useState } from "react";
import {
  useProviderInvoiceCandidateSummary,
  useProviderInvoiceCandidates,
} from "utils/providerAdmissionService";
import { toast } from "react-toastify";

const getApiErrorMessage = (error) =>
  error?.response?.data?.message || error?.response?.data?.errorMessage || error?.message || "";

const PROVIDER_INVOICE_DUPLICATE_ITEMS = "PROVIDER_INVOICE_DUPLICATE_ITEMS";

const CANDIDATE_PAGE_SIZE = 20;

export default function ProviderInvoiceModal({ open, onClose, accountProviderId, onSuccess }) {
  const [generateRemaining, setGenerateRemaining] = useState(false);

  // Per-admission selections: [{admissionId, clientId, clientLabel, termNumbers: []}]
  const [clientSelections, setClientSelections] = useState([]);

  const [addedSearch, setAddedSearch] = useState("");

  const [pendingAdmissionId, setPendingAdmissionId] = useState(null);
  const [pendingTermNumbers, setPendingTermNumbers] = useState([]);

  const [invoiceType, setInvoiceType] = useState("COMMISSION_AND_BONUS");
  // "PER_TERM" (default — per-term component selection) or "WHOLE_INVOICE" (one type for all).
  // Per term is the default because a term's two components are routinely at different stages
  // (commission claimed, bonus still owed), which a single whole-invoice type cannot express.
  const [granularity, setGranularity] = useState("PER_TERM");
  const isPerTerm = granularity === "PER_TERM";
  const [title, setTitle] = useState("");
  const [dueDate, setDueDate] = useState("");

  const [commissionTitle, setCommissionTitle] = useState("");
  const [commissionDueDate, setCommissionDueDate] = useState("");
  const [bonusTitle, setBonusTitle] = useState("");
  const [bonusDueDate, setBonusDueDate] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [duplicateError, setDuplicateError] = useState(null);

  // The server decides what can still be billed: a component that is already invoiced, claimed,
  // waived or worth $0 has nothing left to produce, and offering it only earns a "Duplicate
  // detected" rejection after the form has been filled in. It also means this modal asks for one
  // page of candidates rather than every admission on the provider.
  const [clientSearchInput, setClientSearchInput] = useState("");
  const [clientSearch, setClientSearch] = useState("");
  const [candidatePage, setCandidatePage] = useState(0);

  const serverInvoiceType = isPerTerm ? null : invoiceType;

  const { data: candidatePageData, isFetching: loadingCandidates } = useProviderInvoiceCandidates(
    accountProviderId,
    {
      page: candidatePage,
      size: CANDIDATE_PAGE_SIZE,
      search: clientSearch,
      invoiceType: serverInvoiceType,
    },
    { enabled: open && !generateRemaining }
  );

  const { data: candidateSummary } = useProviderInvoiceCandidateSummary(
    accountProviderId,
    serverInvoiceType,
    { enabled: open && generateRemaining }
  );

  const candidates = useMemo(() => candidatePageData?.content ?? [], [candidatePageData]);
  const candidateTotal = candidatePageData?.totalItems ?? 0;

  useEffect(() => {
    const handle = setTimeout(() => {
      if (clientSearchInput !== clientSearch) {
        setClientSearch(clientSearchInput);
        setCandidatePage(0);
      }
    }, 300);
    return () => clearTimeout(handle);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [clientSearchInput]);

  // Switching the invoice type changes which clients still have something billable, so the page
  // the user was on no longer means the same thing.
  useEffect(() => {
    setCandidatePage(0);
  }, [serverInvoiceType]);

  const candidateLabel = (candidate) => {
    const identifier = candidate.passportNumber || candidate.usiNumber || null;
    const name = candidate.studentName || candidate.clientName || `Client #${candidate.clientId}`;
    return (
      name +
      (candidate.courseName ? ` — ${candidate.courseName}` : "") +
      (identifier ? ` (${identifier})` : "")
    );
  };

  const clientOptions = useMemo(
    () =>
      candidates.map((candidate) => ({
        admissionId: candidate.admissionId,
        clientId: candidate.clientId,
        label: candidateLabel(candidate),
      })),
    [candidates]
  );

  const termsByAdmissionId = useMemo(
    () => Object.fromEntries(candidates.map((c) => [c.admissionId, c.terms ?? []])),
    [candidates]
  );

  const commissionNeedsInvoice = (t) => Boolean(t?.commissionNeedsInvoice);
  const bonusNeedsInvoice = (t) => Boolean(t?.bonusNeedsInvoice);

  /**
   * A selection keeps the terms it was added with, so it stays configurable after the user pages or
   * searches the candidate list past it; anything not yet selected comes from the current page.
   */
  const findTerm = (admissionId, termNumber) => {
    const terms =
      clientSelections.find((sel) => sel.admissionId === admissionId)?.terms ??
      termsByAdmissionId[admissionId] ??
      [];
    return terms.find((t) => t.termNumber === termNumber) || null;
  };

  /**
   * The only component choice that can still produce invoice lines for this term.
   */
  const defaultComponentForTerm = (t) => {
    if (!t) return "COMMISSION_AND_BONUS";
    const commission = commissionNeedsInvoice(t);
    const bonus = bonusNeedsInvoice(t);
    if (commission && bonus) return "COMMISSION_AND_BONUS";
    if (commission) return "COMMISSION_ONLY";
    if (bonus) return "BONUS_ONLY";
    return "COMMISSION_AND_BONUS";
  };

  const defaultComponentFor = (admissionId, termNumber) =>
    defaultComponentForTerm(findTerm(admissionId, termNumber));

  const pendingOption = useMemo(
    () => clientOptions.find((o) => o.admissionId === pendingAdmissionId) || null,
    [clientOptions, pendingAdmissionId]
  );

  const uninvoicedTermsForPending = useMemo(
    () => (pendingAdmissionId ? termsByAdmissionId[pendingAdmissionId] ?? [] : []),
    [termsByAdmissionId, pendingAdmissionId]
  );

  /**
   * "Generate remaining" bills terms this modal never listed, so its warnings come from the server.
   * For an explicit selection the terms are already in hand.
   */
  const zeroComponentNotices = useMemo(() => {
    if (generateRemaining) return candidateSummary?.zeroComponentNotices ?? [];
    const covers = (component) =>
      invoiceType === "COMMISSION_ONLY"
        ? component === "commission"
        : invoiceType === "BONUS_ONLY"
        ? component === "bonus"
        : true;
    const notices = [];
    clientSelections.forEach((sel) => {
      (sel.terms ?? [])
        .filter((t) => sel.termNumbers.includes(t.termNumber))
        .forEach((t) => {
          const commissionZero =
            covers("commission") && (Number(t.commission) || 0) <= 0 && !t.commissionNotApplicable;
          const bonusZero = covers("bonus") && (Number(t.bonus) || 0) <= 0 && !t.bonusNotApplicable;
          if (!commissionZero && !bonusZero) return;
          notices.push({
            label: `${sel.clientLabel} · T${t.termNumber}`,
            kind: commissionZero ? "commission" : "bonus",
          });
        });
    });
    return notices;
  }, [generateRemaining, candidateSummary, clientSelections, invoiceType]);

  const handleClose = () => {
    setGenerateRemaining(false);
    setClientSelections([]);
    setGranularity("PER_TERM");
    setAddedSearch("");
    setClientSearchInput("");
    setClientSearch("");
    setCandidatePage(0);
    setPendingAdmissionId(null);
    setPendingTermNumbers([]);
    setInvoiceType("COMMISSION_AND_BONUS");
    setTitle("");
    setDueDate("");
    setCommissionTitle("");
    setCommissionDueDate("");
    setBonusTitle("");
    setBonusDueDate("");
    setDuplicateError(null);
    onClose();
  };

  const togglePendingTerm = (termNumber) =>
    setPendingTermNumbers((prev) =>
      prev.includes(termNumber) ? prev.filter((n) => n !== termNumber) : [...prev, termNumber]
    );

  const handleAddPending = () => {
    if (!pendingAdmissionId || pendingTermNumbers.length === 0) return;
    setClientSelections((prev) => [
      ...prev,
      {
        admissionId: pendingAdmissionId,
        clientId: pendingOption.clientId,
        clientLabel: pendingOption.label,
        // Kept on the selection, not looked up later: the candidate list is paged and searchable,
        // so this client may not be on screen when their terms are toggled.
        terms: uninvoicedTermsForPending,
        termNumbers: pendingTermNumbers,
        termComponents: Object.fromEntries(
          pendingTermNumbers.map((tn) => [tn, defaultComponentFor(pendingAdmissionId, tn)])
        ),
      },
    ]);
    setPendingAdmissionId(null);
    setPendingTermNumbers([]);
  };

  const handleRemoveSelection = (admissionId) =>
    setClientSelections((prev) => prev.filter((s) => s.admissionId !== admissionId));

  const handleToggleTerm = (admissionId, termNumber) =>
    setClientSelections((prev) =>
      prev.map((s) => {
        if (s.admissionId !== admissionId) return s;
        const termNumbers = s.termNumbers.includes(termNumber)
          ? s.termNumbers.filter((n) => n !== termNumber)
          : [...s.termNumbers, termNumber];
        const termComponents = s.termNumbers.includes(termNumber)
          ? Object.fromEntries(
              Object.entries(s.termComponents || {}).filter(([tn]) => Number(tn) !== termNumber)
            )
          : {
              ...(s.termComponents || {}),
              [termNumber]: defaultComponentFor(admissionId, termNumber),
            };
        return { ...s, termNumbers, termComponents };
      })
    );

  const handleTermComponentChange = (admissionId, termNumber, componentType) =>
    setClientSelections((prev) =>
      prev.map((s) =>
        s.admissionId !== admissionId
          ? s
          : { ...s, termComponents: { ...(s.termComponents || {}), [termNumber]: componentType } }
      )
    );

  const handleGenerate = async () => {
    setDuplicateError(null);
    setSubmitting(true);
    try {
      const isSeparate = !isPerTerm && invoiceType === "COMMISSION_AND_BONUS_SEPARATE";
      const payload = {
        accountProviderId,
        generateRemaining,
        granularity: isPerTerm ? "PER_TERM" : "WHOLE_INVOICE",
        invoiceType: isPerTerm ? null : invoiceType,
        title: isSeparate ? null : title.trim() || null,
        dueDate: isSeparate ? null : dueDate || null,
      };

      if (isSeparate) {
        payload.commissionTitle = commissionTitle.trim() || null;
        payload.commissionDueDate = commissionDueDate || null;
        payload.bonusTitle = bonusTitle.trim() || null;
        payload.bonusDueDate = bonusDueDate || null;
      }

      if (!generateRemaining) {
        payload.clientTermSelections = clientSelections.map((s) => ({
          clientId: s.clientId,
          termNumbers: isPerTerm ? null : s.termNumbers,
          termSelections: isPerTerm
            ? s.termNumbers.map((tn) => ({
                termNumber: tn,
                componentType: s.termComponents?.[tn] || defaultComponentFor(s.admissionId, tn),
              }))
            : null,
        }));
      }

      const res = await axios.post(`${API_URL}/provider-admissions/invoices/generate`, payload);
      const generated = res?.data?.data;
      const count = Array.isArray(generated) ? generated.length : 1;
      toast.success(count > 1 ? `${count} invoices generated` : "Invoice generated");
      onSuccess?.();
      handleClose();
    } catch (e) {
      const msg = getApiErrorMessage(e);
      if (e?.response?.data?.errorCode === PROVIDER_INVOICE_DUPLICATE_ITEMS) {
        setDuplicateError(msg);
        toast.error(msg);
      } else {
        toast.error(msg || "Failed to generate invoice");
      }
    } finally {
      setSubmitting(false);
    }
  };

  const canGenerate = generateRemaining || clientSelections.length > 0;
  const addedAdmissionIds = clientSelections.map((sel) => sel.admissionId);
  const availablePendingOptions = clientOptions.filter(
    (o) => !addedAdmissionIds.includes(o.admissionId)
  );
  const hasMoreCandidatePages = candidateTotal > (candidatePage + 1) * CANDIDATE_PAGE_SIZE;

  const filteredSelections = addedSearch.trim()
    ? clientSelections.filter((s) =>
        s.clientLabel.toLowerCase().includes(addedSearch.toLowerCase())
      )
    : clientSelections;

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
      <DialogTitle>Generate Provider Invoice</DialogTitle>
      <DialogContent>
        {/* Generate remaining toggle */}
        <div className="mb-4 mt-1">
          <FormControlLabel
            control={
              <Checkbox
                checked={generateRemaining}
                onChange={(e) => {
                  const checked = e.target.checked;
                  setGenerateRemaining(checked);
                  if (checked) setGranularity("WHOLE_INVOICE");
                }}
              />
            }
            label={
              <span className="text-sm font-medium">
                Generate all remaining uninvoiced terms for this provider
                {generateRemaining && candidateSummary
                  ? ` (${candidateSummary.termCount} term${
                      candidateSummary.termCount === 1 ? "" : "s"
                    } across ${candidateSummary.clientCount} client${
                      candidateSummary.clientCount === 1 ? "" : "s"
                    })`
                  : ""}
              </span>
            }
          />
        </div>

        {/* Per-client term selection */}
        {!generateRemaining && (
          <div className="flex flex-col gap-3 mb-4 border rounded p-3 bg-app-surface-muted">
            {/* Header row with search */}
            <div className="flex items-center justify-between gap-2">
              <p className="text-xs text-app-text-secondary font-medium uppercase tracking-wide shrink-0">
                Select clients &amp; terms
              </p>
              {clientSelections.length > 0 && (
                <TextField
                  size="small"
                  placeholder="Search added..."
                  value={addedSearch}
                  onChange={(e) => setAddedSearch(e.target.value)}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <Search sx={{ fontSize: 16 }} />
                      </InputAdornment>
                    ),
                  }}
                  sx={{ "& input": { fontSize: 12, py: 0.5 }, maxWidth: 160 }}
                />
              )}
            </div>

            {/* Already-added selections — scrollable list */}
            {clientSelections.length > 0 && (
              <div className="flex flex-col gap-2 max-h-52 overflow-y-auto pr-1">
                {filteredSelections.length === 0 ? (
                  <p className="text-xs text-app-text-muted">No matches for "{addedSearch}"</p>
                ) : (
                  filteredSelections.map((sel) => {
                    const allUninvoiced = sel.terms ?? [];
                    return (
                      <div
                        key={sel.admissionId}
                        className="bg-app-surface border rounded p-2 flex flex-col gap-1 shrink-0"
                      >
                        <div className="flex items-center justify-between">
                          <span className="text-sm font-medium">{sel.clientLabel}</span>
                          <Tooltip title="Remove" arrow>
                            <IconButton
                              size="small"
                              onClick={() => handleRemoveSelection(sel.admissionId)}
                            >
                              <DeleteOutlined fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        </div>
                        <div className="flex flex-col gap-1">
                          {allUninvoiced.map((t) => (
                            <div key={t.id} className="flex items-center gap-2 flex-wrap">
                              <Chip
                                label={`T${t.termNumber}${
                                  t.month || t.year
                                    ? ` · ${[t.month, t.year].filter(Boolean).join("/")}`
                                    : ""
                                }`}
                                size="small"
                                clickable
                                color={
                                  sel.termNumbers.includes(t.termNumber) ? "primary" : "default"
                                }
                                onClick={() => handleToggleTerm(sel.admissionId, t.termNumber)}
                                sx={{ fontSize: 11 }}
                              />
                              {isPerTerm && sel.termNumbers.includes(t.termNumber) && (
                                <ToggleButtonGroup
                                  value={
                                    sel.termComponents?.[t.termNumber] || defaultComponentForTerm(t)
                                  }
                                  exclusive
                                  size="small"
                                  onChange={(_, val) =>
                                    val &&
                                    handleTermComponentChange(sel.admissionId, t.termNumber, val)
                                  }
                                >
                                  <ToggleButton
                                    value="COMMISSION_AND_BONUS"
                                    disabled={!commissionNeedsInvoice(t) || !bonusNeedsInvoice(t)}
                                    title={
                                      !commissionNeedsInvoice(t) || !bonusNeedsInvoice(t)
                                        ? "One component is already settled — invoice the other one on its own."
                                        : undefined
                                    }
                                    sx={{ fontSize: 10, py: 0.25, px: 0.75 }}
                                  >
                                    Both
                                  </ToggleButton>
                                  <ToggleButton
                                    value="COMMISSION_ONLY"
                                    disabled={!commissionNeedsInvoice(t)}
                                    title={
                                      commissionNeedsInvoice(t)
                                        ? undefined
                                        : "Commission is already invoiced, claimed, N/A or $0."
                                    }
                                    sx={{ fontSize: 10, py: 0.25, px: 0.75 }}
                                  >
                                    Commission
                                  </ToggleButton>
                                  <ToggleButton
                                    value="BONUS_ONLY"
                                    disabled={!bonusNeedsInvoice(t)}
                                    title={
                                      bonusNeedsInvoice(t)
                                        ? undefined
                                        : "Bonus is already invoiced, claimed, N/A or $0."
                                    }
                                    sx={{ fontSize: 10, py: 0.25, px: 0.75 }}
                                  >
                                    Bonus
                                  </ToggleButton>
                                </ToggleButtonGroup>
                              )}
                            </div>
                          ))}
                        </div>
                      </div>
                    );
                  })
                )}
              </div>
            )}

            {/* Add new client row */}
            {availablePendingOptions.length > 0 && (
              <div className="bg-app-surface border border-dashed rounded p-2 flex flex-col gap-2">
                <Autocomplete
                  size="small"
                  options={availablePendingOptions}
                  getOptionLabel={(o) => o.label}
                  isOptionEqualToValue={(o, v) => o.admissionId === v.admissionId}
                  value={pendingOption}
                  loading={loadingCandidates}
                  // The server already filtered and paged these; filtering again client-side would
                  // hide matches that simply are not on this page yet.
                  filterOptions={(options) => options}
                  inputValue={clientSearchInput}
                  onInputChange={(_, val, reason) => {
                    if (reason !== "reset") setClientSearchInput(val);
                  }}
                  onChange={(_, val) => {
                    setPendingAdmissionId(val?.admissionId || null);
                    setPendingTermNumbers([]);
                  }}
                  noOptionsText={
                    loadingCandidates ? "Loading…" : "No clients with uninvoiced terms"
                  }
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label="Add client"
                      size="small"
                      placeholder="Type to search all clients…"
                    />
                  )}
                />
                {hasMoreCandidatePages && (
                  <div className="flex items-center justify-between">
                    <span className="text-xs text-app-text-muted">
                      Showing {candidates.length} of {candidateTotal} — search to narrow, or load
                      more.
                    </span>
                    <Button
                      size="small"
                      disabled={loadingCandidates}
                      onClick={() => setCandidatePage((p) => p + 1)}
                    >
                      Next
                    </Button>
                  </div>
                )}

                {pendingAdmissionId && (
                  <>
                    {uninvoicedTermsForPending.length === 0 ? (
                      <p className="text-xs text-app-text-muted">No uninvoiced terms available</p>
                    ) : (
                      <div className="flex flex-wrap gap-1">
                        {uninvoicedTermsForPending.map((t) => (
                          <Chip
                            key={t.id}
                            label={`T${t.termNumber}${
                              t.month || t.year
                                ? ` · ${[t.month, t.year].filter(Boolean).join("/")}`
                                : ""
                            }`}
                            size="small"
                            clickable
                            color={
                              pendingTermNumbers.includes(t.termNumber) ? "primary" : "default"
                            }
                            onClick={() => togglePendingTerm(t.termNumber)}
                            sx={{ fontSize: 11 }}
                          />
                        ))}
                      </div>
                    )}
                    <Button
                      size="small"
                      variant="outlined"
                      startIcon={<Add />}
                      disabled={pendingTermNumbers.length === 0}
                      onClick={handleAddPending}
                      sx={{ alignSelf: "flex-start" }}
                    >
                      Add
                    </Button>
                  </>
                )}
              </div>
            )}

            {clientSelections.length === 0 &&
              availablePendingOptions.length === 0 &&
              !loadingCandidates && (
                <p className="text-xs text-app-text-muted">
                  {clientSearch
                    ? `No clients with uninvoiced terms match "${clientSearch}"`
                    : "No clients with uninvoiced terms available"}
                </p>
              )}
          </div>
        )}

        {!generateRemaining && (
          <div className="mb-4">
            <Typography
              variant="caption"
              sx={{ display: "block", mb: 0.5, color: "text.secondary", fontWeight: 500 }}
            >
              Granularity
            </Typography>
            <ToggleButtonGroup
              value={granularity}
              exclusive
              onChange={(_, val) => val && setGranularity(val)}
              size="small"
            >
              <ToggleButton value="WHOLE_INVOICE">Whole invoice</ToggleButton>
              <ToggleButton value="PER_TERM">Per term</ToggleButton>
            </ToggleButtonGroup>
            {isPerTerm && (
              <p className="text-xs text-app-text-secondary mt-1">
                Pick Both / Commission / Bonus separately for each term below. "Commission + Bonus
                (separate)" isn't available in this mode.
              </p>
            )}
          </div>
        )}

        {!isPerTerm && (
          <div className="mb-4">
            <Typography
              variant="caption"
              sx={{ display: "block", mb: 0.5, color: "text.secondary", fontWeight: 500 }}
            >
              Include in invoice
            </Typography>
            <ToggleButtonGroup
              value={invoiceType}
              exclusive
              onChange={(_, val) => {
                if (val) setInvoiceType(val);
              }}
              size="small"
              sx={{ flexWrap: "wrap" }}
            >
              <ToggleButton value="COMMISSION_AND_BONUS">Commission + Bonus</ToggleButton>
              <ToggleButton value="COMMISSION_AND_BONUS_SEPARATE">
                Commission + Bonus (separate)
              </ToggleButton>
              <ToggleButton value="COMMISSION_ONLY">Commission only</ToggleButton>
              <ToggleButton value="BONUS_ONLY">Bonus only</ToggleButton>
            </ToggleButtonGroup>
            {invoiceType === "COMMISSION_AND_BONUS_SEPARATE" && (
              <p className="text-xs text-app-text-secondary mt-1">
                Two invoices will be generated: one commission-only and one bonus-only.
              </p>
            )}
          </div>
        )}

        {/* Invoice metadata */}
        <div className="flex flex-col gap-3">
          {!isPerTerm && invoiceType === "COMMISSION_AND_BONUS_SEPARATE" ? (
            <>
              <div className="flex flex-col gap-3 border rounded p-3">
                <p className="text-xs font-semibold text-app-text-secondary uppercase tracking-wide">
                  Commission invoice
                </p>
                <TextField
                  label="Commission Invoice Title (optional)"
                  size="small"
                  value={commissionTitle}
                  onChange={(e) => setCommissionTitle(e.target.value)}
                  fullWidth
                />
                <TextField
                  label="Commission Due Date (optional)"
                  type="date"
                  size="small"
                  value={commissionDueDate}
                  onChange={(e) => setCommissionDueDate(e.target.value)}
                  fullWidth
                  InputLabelProps={{ shrink: true }}
                />
              </div>
              <div className="flex flex-col gap-3 border rounded p-3">
                <p className="text-xs font-semibold text-app-text-secondary uppercase tracking-wide">
                  Bonus invoice
                </p>
                <TextField
                  label="Bonus Invoice Title (optional)"
                  size="small"
                  value={bonusTitle}
                  onChange={(e) => setBonusTitle(e.target.value)}
                  fullWidth
                />
                <TextField
                  label="Bonus Due Date (optional)"
                  type="date"
                  size="small"
                  value={bonusDueDate}
                  onChange={(e) => setBonusDueDate(e.target.value)}
                  fullWidth
                  InputLabelProps={{ shrink: true }}
                />
              </div>
            </>
          ) : (
            <>
              <TextField
                label="Invoice Title (optional)"
                size="small"
                value={title}
                onChange={(e) => setTitle(e.target.value)}
                fullWidth
              />
              <TextField
                label="Due Date (optional)"
                type="date"
                size="small"
                value={dueDate}
                onChange={(e) => setDueDate(e.target.value)}
                fullWidth
                InputLabelProps={{ shrink: true }}
              />
            </>
          )}
          {/* <TextField label="Authorized By (optional)" size="small" fullWidth /> */}
        </div>

        {/* Zero-component warning — non-blocking */}
        {zeroComponentNotices.length > 0 && (
          <div className="mt-3 p-3 bg-amber-50 border border-amber-200 rounded text-xs text-amber-800">
            <strong>Heads up:</strong> some selected terms have a $0 component. It won't be invoiced
            — mark it not-applicable on the term if that's intended, or set an amount.
            <ul className="mt-1 list-disc pl-4">
              {zeroComponentNotices.slice(0, 6).map((n, i) => (
                <li key={i}>
                  {n.label} — {n.kind} is $0
                </li>
              ))}
              {zeroComponentNotices.length > 6 && <li>+{zeroComponentNotices.length - 6} more…</li>}
            </ul>
          </div>
        )}

        {/* Duplicate error */}
        {duplicateError && (
          <div className="mt-3 p-3 bg-red-50 border border-red-200 rounded text-sm text-red-700">
            <strong>Duplicate detected:</strong> {duplicateError}
          </div>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={handleClose} disabled={submitting}>
          Cancel
        </Button>
        <Button
          variant="contained"
          onClick={handleGenerate}
          disabled={submitting || !canGenerate}
          startIcon={submitting ? <CircularProgress size={14} /> : null}
        >
          {submitting ? "Generating..." : "Generate"}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
